import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeft,
    MapPin,
    Shield,
    PlayCircle,
    Edit,
    Trash2,
    Loader2,
    FileText,
    ChevronDown,
    ChevronUp,
    Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import PolygonValidator from '../components/polygons/PolygonValidator';
import PolygonEditModal from '../components/polygons/PolygonEditModal';
import PolygonDetailsModal from '../components/polygons/PolygonDetailsModal';
import AnalysisModal from '../components/polygons/AnalysisModal';
import PolygonMap from '../components/map/PolygonMap';
import { ValidationBadge, AnalysisBadge } from '../components/polygons/StatusBadge';
import AnalysisJobsList from '../components/analysis/AnalysisJobsList';
import PolygonDownloadMenu from '../components/polygons/PolygonDownloadMenu';
import { polygonsApi, getErrorMessage } from '../lib/api';

export default function PolygonWorkspacePage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [polygon, setPolygon] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal states
    const [showValidator, setShowValidator] = useState(false);
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showJobs, setShowJobs] = useState(true);

    useEffect(() => {
        if (id) fetchPolygon();
    }, [id]);

    const fetchPolygon = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await polygonsApi.get(id);
            setPolygon(data);
        } catch (err: any) {
            setError(getErrorMessage(err) || 'Error loading polygon');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!polygon || !id) return;
        if (!confirm(`Delete polygon "${polygon.name}"?`)) return;
        try {
            await polygonsApi.delete(id);
            toast.success('Polygon deleted');
            navigate('/polygons');
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error deleting polygon');
        }
    };

    const handleAnalysisSuccess = async () => {
        toast.success('Analysis started');
        setShowAnalysis(false);
        await fetchPolygon();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (error || !polygon) {
        return (
            <div className="space-y-4 animate-fade-in">
                <button onClick={() => navigate('/polygons')} className="flex items-center gap-2 text-primary-600 hover:text-primary-700">
                    <ArrowLeft className="w-5 h-5" />
                    {t('polygons.backToPortfolio')}
                </button>
                <div className="card p-6 border-red-200">
                    <p className="text-red-600">{error || 'Polygon not found'}</p>
                </div>
            </div>
        );
    }

    // Extract raw geometry, handling Feature/FeatureCollection wrappers stored in DB
    const extractGeometry = (geom: any): any => {
        if (!geom) return null;
        if (geom.type === 'Feature') return geom.geometry;
        if (geom.type === 'FeatureCollection') return geom.features?.[0]?.geometry;
        return geom;
    };

    const rawGeometry = polygon.geometry
        ? extractGeometry(typeof polygon.geometry === 'string' ? JSON.parse(polygon.geometry) : polygon.geometry)
        : null;

    const mapFeatures = rawGeometry
        ? [
              {
                  type: 'Feature' as const,
                  properties: { id: polygon.id, name: polygon.name, commodity: polygon.commodity, status: polygon.status || 'active', area_ha: polygon.area_ha },
                  geometry: rawGeometry,
              },
          ]
        : [];

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Back nav */}
            <button
                onClick={() => navigate('/polygons')}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
            >
                <ArrowLeft className="w-5 h-5" />
                {t('polygons.backToPortfolio')}
            </button>

            {/* Polygon header */}
            <div className="card p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                                {polygon.name}
                            </h1>
                            <p className="text-sm text-gray-500">
                                {polygon.producer_name && `${polygon.producer_name} · `}
                                {polygon.area_ha?.toFixed(2)} ha ·{' '}
                                {[polygon.region, polygon.country].filter(Boolean).join(', ')} ·{' '}
                                <span className="capitalize">{polygon.commodity}</span>
                            </p>
                            {(polygon.legal_name || polygon.organization) && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {polygon.legal_name && <span>{polygon.legal_name}</span>}
                                    {polygon.legal_name && polygon.organization && ' · '}
                                    {polygon.organization && <span>{polygon.organization}</span>}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <ValidationBadge status={polygon.validation_status || 'none'} size="md" />
                            <AnalysisBadge status={polygon.analysis_status || 'none'} size="md" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Two-column layout: actions + map */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Actions panel */}
                <div className="space-y-4">
                    {/* Core actions - MVP flow */}
                    <div className="card p-4 space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                            {t('workspace.actions')}
                        </h3>

                        <button
                            onClick={() => setShowValidator(!showValidator)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-line dark:border-gray-700 hover:bg-cream dark:hover:bg-gray-800 transition-colors text-left"
                        >
                            <Shield className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                            <div className="flex-1">
                                <p className="font-medium text-sm text-gray-900 dark:text-white">
                                    {t('workspace.validate')}
                                </p>
                                <p className="text-xs text-gray-500">{t('workspace.validateDescription')}</p>
                            </div>
                        </button>

                        <button
                            onClick={() => setShowAnalysis(true)}
                            disabled={polygon.analysis_status === 'processing'}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                                polygon.analysis_status === 'processing'
                                    ? 'border-line dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                                    : 'border-line dark:border-gray-700 hover:bg-cream dark:hover:bg-gray-800'
                            }`}
                        >
                            <PlayCircle className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                            <div className="flex-1">
                                <p className="font-medium text-sm text-gray-900 dark:text-white">
                                    {polygon.analysis_status === 'processing' ? t('workspace.analyzing') : t('workspace.analyze')}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {polygon.analysis_status === 'processing'
                                        ? t('workspace.analysisInProgress')
                                        : t('workspace.analyzeDescription')}
                                </p>
                            </div>
                        </button>

                        <button
                            onClick={() => navigate('/reports')}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-line dark:border-gray-700 hover:bg-cream dark:hover:bg-gray-800 transition-colors text-left"
                        >
                            <FileText className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                            <div className="flex-1">
                                <p className="font-medium text-sm text-gray-900 dark:text-white">
                                    {t('workspace.reports')}
                                </p>
                                <p className="text-xs text-gray-500">{t('workspace.reportsDescription')}</p>
                            </div>
                        </button>
                    </div>

                    {/* Secondary actions */}
                    <div className="card p-4 space-y-2">
                        <button
                            onClick={() => setShowDetails(true)}
                            className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm text-gray-600 dark:text-gray-400"
                        >
                            <Eye className="w-4 h-4" />
                            View details
                        </button>
                        <button
                            onClick={() => setShowEdit(true)}
                            className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm text-gray-600 dark:text-gray-400"
                        >
                            <Edit className="w-4 h-4" />
                            Edit metadata
                        </button>
                        <PolygonDownloadMenu polygonId={polygon.id} polygonName={polygon.name} fullWidth />
                        <button
                            onClick={handleDelete}
                            className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm text-red-600"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete polygon
                        </button>
                    </div>

                    {/* Analysis jobs for this polygon */}
                    <div className="card p-4">
                        <button
                            onClick={() => setShowJobs(!showJobs)}
                            className="w-full flex items-center justify-between text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider"
                        >
                            Analysis Jobs
                            {showJobs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {showJobs && (
                            <div className="mt-3">
                                <AnalysisJobsList polygonId={id} autoRefresh refreshInterval={5000} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Map */}
                <div className="lg:col-span-2">
                    <div className="card overflow-hidden h-[55vh] lg:h-[calc(100vh-320px)]">
                        <PolygonMap
                            features={mapFeatures}
                            selectedPolygonId={polygon.id}
                            height="100%"
                        />
                    </div>
                </div>
            </div>

            {/* Validation section (collapsible) */}
            {showValidator && (
                <div className="card p-6">
                    <PolygonValidator polygonId={polygon.id} />
                </div>
            )}

            {/* Modals */}
            {showAnalysis && (
                <AnalysisModal
                    polygon={polygon}
                    onClose={() => setShowAnalysis(false)}
                    onSuccess={handleAnalysisSuccess}
                />
            )}
            {showEdit && (
                <PolygonEditModal
                    polygon={polygon}
                    onClose={() => setShowEdit(false)}
                    onSuccess={fetchPolygon}
                />
            )}
            {showDetails && (
                <PolygonDetailsModal
                    polygon={polygon}
                    onClose={() => setShowDetails(false)}
                />
            )}
        </div>
    );
}
